import { camposVisibles } from './escalasEvolucion';

// ── Datos que dos sistemas preguntan ─────────────────────────────────────────
// Decisión del fundador (2026-09-29): los glóbulos blancos (infeccioso y
// hemato) y el lactato (infeccioso y la microhemodinamia) se QUEDAN en los dos
// sistemas, "aunque si se colocó arriba, ya debería venir". Lo escrito en uno
// llega lleno al otro, que es la regla de la casa de no pedir un dato dos
// veces sin mover el campo de donde se lee.
//
// La fiebre también está en dos sistemas (temperatura en hemodinamia, estado
// febril en infeccioso) pero NO se espeja: sus opciones no calzan una a una
// ("Peaks febriles" no dice cómo está la temperatura ahora), y ante la
// ambigüedad, silencio.
//
// Reglas (las primeras las ganaron los puentes, turnoEvolucionBridge.js; las
// demás, la auditoría adversarial del mismo día):
//  · Solo se mueve algo cuando la autora ACABA de escribir la fuente (o de
//    abrir la puerta del destino). Un cambio en otro campo no reparte nada:
//    sin esto, el GB que ella borraba en infeccioso volvía desde hemato en la
//    siguiente tecla que diera en cualquier parte.
//  · Jamás se escribe en el campo que ella acaba de tocar (borrar la copia es
//    borrarla).
//  · La copia es SUYA solo mientras sigue intacta: el libro `_compartidos`
//    guarda el valor exacto que escribió cada espejo. Si ella la cambió, manda
//    ella. Si ella vacía la fuente y la copia sigue intacta, la copia se va
//    con ella (sin eso, un "14" borrado tecla a tecla dejaba un GB "1" en los
//    dos sistemas).
//  · No escribe en un sistema con la basal firmada ("Evaluado, sin
//    hallazgos").
//  · Solo abre una puerta que no afirma nada nuevo: el GB abre "Con
//    laboratorio del día" si nadie la respondió (tener un GB es tener
//    laboratorio), y la vuelve a cerrar si la abrió él y se queda vacía.
//    "Monitoreo avanzado" NUNCA se abre por un lactato: diría PiCCO o
//    Swan-Ganz. Ahí el lactato llega cuando la autora abre la
//    microhemodinamia ella misma.
export const DATOS_COMPARTIDOS = [
  { a: { sistema: 'infecto', k: 'gb' }, b: { sistema: 'hemato', k: 'gb' } },
  { a: { sistema: 'infecto', k: 'lactico' }, b: { sistema: 'hemodinamia', k: 'lactato' } },
];

// Puerta de cada destino que la tiene: abrirla trae el dato si estaba en el
// otro sistema. `abre`: si el espejo puede abrirla él (y qué campos viven
// detrás, para saber si puede volver a cerrarla).
const PUERTAS = {
  'hemato.gb': { k: 'labHemato', valor: 'Con laboratorio del día', abre: true, detras: ['hb', 'plaquetas', 'gb', 'ran'] },
  'hemodinamia.lactato': { k: 'monitoreoAvanzado', valor: 'Con monitoreo avanzado', abre: false },
};

const lleno = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const clave = (p) => `${p.sistema}.${p.k}`;
const seVe = (sistema, campos, k) => camposVisibles(sistema, campos).some((c) => c.k === k);

// `editados`: claves "sistema.k" que la autora (o su dictado, o la
// reconciliación del dictado) acaba de escribir. Pura: devuelve la MISMA
// referencia si no hay nada que hacer.
export function espejarCompartidos(evolucion, editados = []) {
  if (!evolucion || typeof evolucion !== 'object' || !editados.length) return evolucion;
  let evo = evolucion;
  const libro = { ...(evolucion._compartidos || {}) };
  let cambio = false;
  const escribir = (sistema, st, campos) => {
    evo = { ...evo, sistemas: { ...(evo.sistemas || {}), [sistema]: { ...st, campos } } };
    cambio = true;
  };

  for (const par of DATOS_COMPARTIDOS) {
    for (const [src, dst] of [[par.a, par.b], [par.b, par.a]]) {
      const kDst = clave(dst);
      if (editados.includes(kDst)) continue; // lo acaba de tocar ella
      const puerta = PUERTAS[kDst];
      const srcEditado = editados.includes(clave(src));
      const puertaEditada = !!puerta && editados.includes(`${dst.sistema}.${puerta.k}`);
      if (!srcEditado && !puertaEditada) continue;

      const sistemas = evo.sistemas || {};
      const camposSrc = sistemas[src.sistema]?.campos || {};
      const valor = seVe(src.sistema, camposSrc, src.k) ? camposSrc[src.k] : undefined;
      const stDst = sistemas[dst.sistema] || {};
      const camposDst = stDst.campos || {};
      const actual = camposDst[dst.k];
      const copiaIntacta = lleno(actual) && libro[kDst] === actual;

      // Ella vació la fuente: la copia intacta se va con ella (y la puerta
      // que abrió el espejo, si quedó sin nada detrás).
      if (!lleno(valor)) {
        if (!srcEditado || !copiaIntacta) continue;
        const campos = { ...camposDst };
        delete campos[dst.k];
        delete libro[kDst];
        const kPuerta = puerta ? `puerta:${dst.sistema}.${puerta.k}` : null;
        if (kPuerta && libro[kPuerta] === campos[puerta.k]
          && !(puerta.detras || []).some((k) => lleno(campos[k]))) {
          delete campos[puerta.k];
        }
        if (kPuerta) delete libro[kPuerta];
        escribir(dst.sistema, stDst, campos);
        continue;
      }

      if (stDst.sinHallazgos === true) continue;
      if (String(actual ?? '') === String(valor)) continue;
      // Solo se actualiza una copia propia intacta, y solo cuando su fuente es
      // lo que ella acaba de editar; abrir la puerta únicamente llena vacíos.
      if (lleno(actual) && !(srcEditado && copiaIntacta)) continue;

      let campos = { ...camposDst, [dst.k]: valor };
      if (!seVe(dst.sistema, campos, dst.k)) {
        // Una puerta ya respondida manda ("Sin laboratorio nuevo" es su palabra).
        if (!puerta?.abre || lleno(camposDst[puerta.k])) continue;
        campos = { ...campos, [puerta.k]: puerta.valor };
        if (!seVe(dst.sistema, campos, dst.k)) continue;
        libro[`puerta:${dst.sistema}.${puerta.k}`] = puerta.valor;
      }
      libro[kDst] = valor;
      escribir(dst.sistema, stDst, campos);
    }
  }
  if (!cambio) return evolucion;
  const out = { ...evo };
  if (Object.keys(libro).length) out._compartidos = libro; else delete out._compartidos;
  return out;
}
